<template>
  <div class="surface-50 py-5 px-3 md:px-5">
    <Card class="w-full md:w-10 lg:w-8 mx-auto shadow-1">
      <template #content>
        <div class="grid align-items-start">
          <div class="col-12 md:col-4 flex justify-content-center md:justify-content-start">
            <img
              v-if="item?.img"
              :src="`/media/${item.img}.webp`"
              :alt="`Portrait of ${displayName}`"
              class="faculty-photo w-full max-w-18rem border-round"
            />
            <div
              v-else
              class="faculty-photo w-full max-w-18rem border-round surface-100 flex align-items-center justify-content-center"
              role="img"
              :aria-label="`No portrait available for ${displayName}`"
            >
              <i class="pi pi-user text-6xl text-400" />
            </div>
          </div>

          <div class="col-12 md:col-8 md:pl-5">
            <div class="flex justify-content-between align-items-center">
              <h1 class="text-3xl md:text-4xl text-900 mt-2 mb-3">{{ displayName }}</h1>
              <NuxtLink
                v-if="orcidURL"
                :to="orcidURL"
                external
                target="_blank"
                rel="noopener noreferrer"
                class="inline-block no-underline text-primary font-medium hover:underline focus:underline"
                :aria-label="`Academic CV for ${displayName} (opens in a new tab)`"
              >
                Academic CV
              </NuxtLink>
            </div>

            <div v-if="profileTagNames.length" class="flex flex-wrap gap-2 mb-3">
              <Tag
                v-for="tagName in profileTagNames"
                :key="tagName"
                :value="tagName"
                rounded
                :pt="{
                  root: {
                    class: 'bg-primary-50 text-primary',
                  },
                }"
              />
            </div>

            <dl v-if="profileDetails.length" class="grid mt-0 mb-0">
              <div v-for="detail in profileDetails" :key="detail.label" class="col-12 sm:col-6">
                <div
                  class="surface-50 border-1 border-100 border-round p-3 h-full flex align-items-start gap-3"
                >
                  <Avatar
                    :icon="detail.icon"
                    shape="circle"
                    class="surface-100 text-primary flex-shrink-0"
                  />
                  <div class="min-w-0">
                    <dt class="text-sm font-semibold text-600 mb-1">{{ detail.label }}</dt>
                    <dd class="text-900 font-medium line-height-3 m-0">{{ detail.value }}</dd>
                  </div>
                </div>
              </div>
            </dl>

            <template v-if="registrationGroups.length">
              <Divider />
              <section aria-labelledby="programme-responsibilities-heading">
                <h2 id="programme-responsibilities-heading" class="text-xl text-900 mt-0 mb-3">
                  Programme responsibilities
                </h2>

                <div class="flex flex-column gap-3">
                  <div
                    v-for="group in registrationGroups"
                    :key="group.roleName"
                    class="surface-50 border-round p-3"
                  >
                    <h3 class="text-base text-900 mt-0 mb-2">{{ group.roleName }}</h3>
                    <ul class="list-none p-0 m-0 flex flex-column gap-2">
                      <li v-for="programme in group.programmes" :key="programme.id">
                        <NuxtLink
                          :to="programme.link"
                          class="flex align-items-center gap-2 text-sm text-primary no-underline hover:underline focus:underline"
                        >
                          <i class="pi pi-book text-xs" aria-hidden="true" />
                          <span>{{ programme.name }}</span>
                        </NuxtLink>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            </template>
          </div>
        </div>

        <template v-if="person?.bio">
          <Divider />
          <section aria-labelledby="biography-heading">
            <h2 id="biography-heading" class="text-xl text-900 mb-3">Biography</h2>
            <div class="text-700 line-height-3" v-html="person.bio" />
          </section>
        </template>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { getPersonDisplayName } from '~/composables/useUtils'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
})
const displayName = computed(() => getPersonDisplayName(props.item.person_title, props.item.name))
const orcidURL = computed(() => {
  const orcidID = typeof props.item.orcid_id === 'string' ? props.item.orcid_id.trim() : ''

  return /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/i.test(orcidID)
    ? `https://orcid.org/${orcidID.toUpperCase()}`
    : ''
})
const isStaffProfile = computed(() => props.item.path === 'staff')
const positionNames = computed(() => {
  const positionName = props.item.position_name
  const names = Array.isArray(positionName) ? positionName : [positionName]

  return [...new Set(names.filter(Boolean))]
})
const profileRoleIDs = computed(() => (props.item.role_ids || []).filter(Boolean))
const staffTypeIDs = computed(() => {
  const staffTypeID = props.item.staff_type_id
  const staffTypeIDs = Array.isArray(staffTypeID) ? staffTypeID : [staffTypeID]

  return [...new Set(staffTypeIDs.filter((id) => id !== null && id !== undefined))]
})

const roleIDs = props.item.registrations
  ? Object.keys(props.item.registrations).map((id) => Number(id))
  : []
const programmeIDs = props.item.registrations
  ? [...new Set(Object.values(props.item.registrations).flat())]
  : []

const personRequest = useAsyncData(`${props.item.person_id}-person`, () => {
  return queryContent('person').where({ id: props.item.person_id }).only(['bio']).findOne()
})

const profileRolesRequest = useAsyncData(
  `${isStaffProfile.value ? 'staff' : 'faculty'}-${props.item.person_id}-profile-roles`,
  () => {
    if (isStaffProfile.value || !profileRoleIDs.value.length) return []

    return queryContent('role')
      .where({
        _path: {
          $regex: new RegExp(`^\/role\/.*`),
        },
        id: { $in: profileRoleIDs.value },
      })
      .only(['name'])
      .find()
  }
)

const staffTypesRequest = useAsyncData(
  `${staffTypeIDs.value.join('-') || 'none'}-staff-types`,
  () => {
    if (!isStaffProfile.value || !staffTypeIDs.value.length) return []

    return queryContent('staff_type')
      .where({ id: { $in: staffTypeIDs.value } })
      .sort({ name: 1 })
      .only(['id', 'name'])
      .find()
  }
)

const qualificationTypeRequest = useAsyncData(
  `${props.item.qualification_type_id || 'none'}-qualification_type`,
  () => {
    if (!props.item.qualification_type_id) return {}

    return queryContent('qualification_type')
      .where({
        _path: {
          $regex: new RegExp(`^\/qualification_type\/.*`),
        },
        id: props.item.qualification_type_id,
      })
      .only(['name'])
      .findOne()
  }
)

const registrationRolesRequest = useAsyncData(`${roleIDs}-regRoles`, () => {
  if (!roleIDs.length) return []

  return queryContent('role')
    .where({
      _path: {
        $regex: new RegExp(`^\/role\/.*`),
      },
      id: { $in: roleIDs },
    })
    .only(['id', 'name'])
    .find()
})

const registrationProgrammesRequest = useAsyncData(`${programmeIDs}-regProgrammes`, () => {
  if (!programmeIDs.length) return []

  return queryContent('programmes')
    .where({
      _path: {
        $regex: new RegExp(`^\/programmes\/.*`),
      },
      id: { $in: programmeIDs },
    })
    .only(['id', 'name', 'link'])
    .find()
})

const [
  { data: person },
  { data: profileRoles },
  { data: staffTypes },
  { data: qualification_type },
  { data: regRoles },
  { data: regProgrammes },
] = await Promise.all([
  personRequest,
  profileRolesRequest,
  staffTypesRequest,
  qualificationTypeRequest,
  registrationRolesRequest,
  registrationProgrammesRequest,
])

const roleNames = computed(() => flatten(profileRoles.value || [], 'name'))
const profileTagNames = computed(() =>
  isStaffProfile.value ? positionNames.value : roleNames.value
)
const staffTypeNames = computed(() => (staffTypes.value || []).map(({ name }) => name))

const profileDetails = computed(() => {
  const details = []

  if (isStaffProfile.value && staffTypeNames.value.length) {
    details.push({
      label: staffTypeNames.value.length === 1 ? 'Staff category' : 'Staff categories',
      icon: 'pi pi-briefcase',
      value: staffTypeNames.value.join(', '),
    })
  }

  if (qualification_type.value?.name) {
    details.push({
      label: 'Highest qualification',
      icon: 'pi pi-graduation-cap',
      value: qualification_type.value.name,
    })
  }

  return details
})

const roleMap = computed(() => {
  return (regRoles.value || []).reduce((acc, role) => {
    acc[role.id] = role.name
    return acc
  }, {})
})

const programmeMap = computed(() => {
  return (regProgrammes.value || []).reduce((acc, programme) => {
    acc[programme.id] = programme
    return acc
  }, {})
})

const registrationGroups = computed(() => {
  return Object.entries(props.item.registrations || {})
    .map(([roleId, programmeIds]) => ({
      roleName: roleMap.value[roleId],
      programmes: [...new Set(programmeIds)]
        .map((programmeId) => programmeMap.value[programmeId])
        .filter(Boolean),
    }))
    .filter((group) => group.roleName && group.programmes.length)
})
</script>

<style scoped>
.faculty-photo {
  aspect-ratio: 1 / 1;
  object-fit: cover;
  object-position: top center;
}
</style>
